const REM_IN_PX = 16;

const convertRemToPx = (value: string) =>
  value.replace(/(-?\d*\.?\d+)rem\b/gi, (_, numericValue: string) => {
    const parsed = Number.parseFloat(numericValue);
    if (!Number.isFinite(parsed)) return `${numericValue}rem`;
    const pxValue = Number((parsed * REM_IN_PX).toFixed(2));
    return `${pxValue}px`;
  });

export const sanitizeRichTextHtml = (html: string) => {
  return html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
    .replace(/\son\w+=("[^"]*"|'[^']*'|[^\s>]+)/gi, "")
    .replace(/style=(["'])(.*?)\1/gi, (_match, quote: string, styleValue: string) => {
      const normalizedStyle = convertRemToPx(styleValue);
      return `style=${quote}${normalizedStyle}${quote}`;
    })
    .trim();
};

export const stripRichTextHtml = (html: string) =>
  sanitizeRichTextHtml(html)
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const PLACEHOLDER_REGEX = /\{([A-Z0-9_]+)\}/g;

export const extractRichTextPlaceholders = (input: string) => {
  const keys = new Set<string>();
  for (const match of input.matchAll(PLACEHOLDER_REGEX)) {
    const key = match[1]?.trim();
    if (key) keys.add(key);
  }
  return Array.from(keys);
};

export const getUnknownRichTextPlaceholders = (input: string, allowedKeys: Set<string>) =>
  extractRichTextPlaceholders(input).filter((key) => !allowedKeys.has(key));

export const countRichTextPlaceholderOccurrences = (input: string, key: string) => {
  const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const matches = input.match(new RegExp(`\\{${escapedKey}\\}`, "g"));
  return matches ? matches.length : 0;
};

export const remapRichTextPlaceholders = (input: string, mapping: Record<string, string>) =>
  input.replace(PLACEHOLDER_REGEX, (_, key: string) => {
    const nextKey = mapping[key]?.trim();
    return nextKey ? `{${nextKey}}` : `{${key}}`;
  });

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

export const applyRichTextPlaceholders = (
  input: string,
  values: Record<string, string>,
) =>
  input.replace(PLACEHOLDER_REGEX, (_, key: string) => {
    const value = values[key] ?? "";
    return escapeHtml(value).replace(/\n/g, "<br />");
  });
